import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaFileAlt,
  FaFileArchive,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFilePowerpoint,
  FaFileWord
} from 'react-icons/fa';
import { documentosDeConvocatoria } from '../services/anexosConvocatoria';
import './boton-documentos.scss';

// Botón de documentos del listado del SEACE (el del icono de PDF, junto al de
// calendario y el de WhatsApp).
//
//   · sin documentos  → deshabilitado, como antes;
//   · con uno solo    → lo abre directo, sin pasos de más;
//   · con varios      → despliega un panel para elegir cuál abrir.
//
// El panel va en un portal con `position: fixed`: dentro de la fila lo
// recortaría el `overflow` de la tabla.
export const ICONOS_DOC = {
  excel: { Icono: FaFileExcel, color: '#1d6f42' },
  pdf: { Icono: FaFilePdf, color: '#b42318' },
  word: { Icono: FaFileWord, color: '#1b5ebe' },
  zip: { Icono: FaFileArchive, color: '#8a5308' },
  ppt: { Icono: FaFilePowerpoint, color: '#c43e1c' },
  plano: { Icono: FaFileAlt, color: '#0f766e' },
  imagen: { Icono: FaFileImage, color: '#6b3fb0' },
  otro: { Icono: FaFileAlt, color: '#5b6470' }
};

const ANCHO_PANEL = 300;

const BotonDocumentos = ({ proceso, onAbrir, className = '', titulo = 'Documentos' }) => {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);
  const boton = useRef(null);

  const documentos = documentosDeConvocatoria(proceso);
  const varios = documentos.length > 1;

  const cerrar = useCallback(() => {
    setAbierto(false);
    setPos(null);
  }, []);

  // El panel es `fixed`: si la página se desplaza dejaría de apuntar al botón,
  // así que en ese caso se cierra en vez de quedar flotando fuera de sitio.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (!boton.current?.contains(e.target) && !e.target.closest?.('.bdoc-panel')) cerrar();
    };
    const escape = (e) => { if (e.key === 'Escape') cerrar(); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto, cerrar]);

  const alPulsar = () => {
    if (documentos.length === 0) return;
    // Con un solo documento el selector sobraría.
    if (documentos.length === 1) {
      onAbrir(documentos[0].url);
      return;
    }

    const caja = boton.current.getBoundingClientRect();
    // Se ancla a la derecha del botón y se repliega si no cabe en la ventana.
    const izquierda = Math.max(
      8,
      Math.min(caja.right - ANCHO_PANEL, window.innerWidth - ANCHO_PANEL - 8)
    );
    const alto = Math.min(documentos.length * 58 + 46, 320);
    const cabeDebajo = caja.bottom + alto + 12 < window.innerHeight;

    setPos({
      left: izquierda,
      top: cabeDebajo ? caja.bottom + 6 : Math.max(8, caja.top - alto - 6)
    });
    setAbierto(true);
  };

  const abrirDocumento = (url) => {
    cerrar();
    onAbrir(url);
  };

  return (
    <>
      <button
        type="button"
        ref={boton}
        className={`${className} bdoc-boton`}
        title={
          documentos.length === 0
            ? 'Sin documentos disponibles'
            : varios
              ? `${titulo} (${documentos.length})`
              : documentos[0].nombre
        }
        aria-label={varios ? `Ver los ${documentos.length} documentos` : titulo}
        aria-haspopup={varios ? 'menu' : undefined}
        aria-expanded={varios ? abierto : undefined}
        disabled={documentos.length === 0}
        onClick={alPulsar}
      >
        <FaFilePdf size={17} aria-hidden="true" />
        {varios && <span className="bdoc-badge">{documentos.length}</span>}
      </button>

      {abierto && pos && createPortal(
        <div className="bdoc-panel" style={{ left: pos.left, top: pos.top, width: ANCHO_PANEL }} role="menu">
          <div className="bdoc-panel__head">
            Documentos
            <span>{documentos.length}</span>
          </div>
          <div className="bdoc-panel__lista">
            {documentos.map((doc) => {
              const { Icono, color } = ICONOS_DOC[doc.tipo] || ICONOS_DOC.otro;
              return (
                <button
                  type="button"
                  key={doc.url}
                  className="bdoc-item"
                  role="menuitem"
                  onClick={() => abrirDocumento(doc.url)}
                >
                  <span className="bdoc-item__ic" style={{ color }}>
                    <Icono size={17} aria-hidden="true" />
                  </span>
                  <span className="bdoc-item__txt">
                    <span className="bdoc-item__nombre">{doc.nombre}</span>
                    <span className="bdoc-item__sub">{doc.etiqueta}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default BotonDocumentos;
